import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FaGithub } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { SiGmail } from "react-icons/si";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { MdEmail, MdArrowBack } from "react-icons/md";
import { ZarazEvents } from "@/utils/zaraz-events";
import { useToast } from "@/components/ui/use-toast";
import { useEffect } from "react";

type EmailFormData = {
  email: string;
};

type CodeFormData = {
  code: string;
};

export default function Login() {
  const [searchParams] = useSearchParams();
  const [isEmailMode, setIsEmailMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const { toast } = useToast();
  const lastUsedAuthnProvider = localStorage.getItem("lastUsedAuthnProvider");

  // Handle invitation token from URL
  useEffect(() => {
    const invitationToken = searchParams.get("invitationToken");
    if (invitationToken) {
      localStorage.setItem("pendingInvitationToken", invitationToken);
    }
    
    // Handle magic link token from URL
    const token = searchParams.get("token");
    if (token) {
      // Redirect to magic link callback to process the token
      window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/callback?token=${encodeURIComponent(token)}`;
    }
  }, [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormData>();
  
  const {
    register: registerCode,
    handleSubmit: handleSubmitCode,
    formState: { errors: codeErrors },
  } = useForm<CodeFormData>();
  const handleGitHubLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "github");
    window.zaraz.track(ZarazEvents.LOGIN_CLICK, { provider: "github" });
    window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/github/login?${searchParams.toString()}`;
  };
  const handleGoogleLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "google");
    window.zaraz.track(ZarazEvents.LOGIN_CLICK, { provider: "google" });
    window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/google/login?${searchParams.toString()}`;
  };

  const handleEmailLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "email");
    window.zaraz.track(ZarazEvents.LOGIN_CLICK, { provider: "email" });
    setIsEmailMode(true);
  };

  const handleBackToSocial = () => {
    setIsEmailMode(false);
  };

  const onSubmitEmail = async (data: EmailFormData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/login?${searchParams.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email,
        }),
      });

      if (response.ok) {
        setIsEmailSent(true);
        setSentEmail(data.email);
        toast({
          title: "Code sent!",
          description: "We've sent a 6-digit code to your email.",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: "Uh oh!",
          description: errorData.error || "Failed to send code. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Uh oh!",
        description: "An error occurred. Please try again. " + JSON.stringify(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitCode = async (data: CodeFormData) => {
    setIsSubmitting(true);
    try {
      // Redirect to magic link callback with the signup code
      window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/callback?signup_code=${encodeURIComponent(data.code)}&${searchParams.toString()}`;
    } catch (error) {
      toast({
        title: "Uh oh!",
        description: "An error occurred. Please try again. " + JSON.stringify(error),
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const lastUsedText = <span className="text-muted-foreground text-xs">(last used)</span>;
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Welcome!</CardTitle>
          <CardDescription>
            {isEmailMode ? "Sign in with your email" : "Choose a sign-in method"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEmailMode ? (
            <div className="space-y-4">
              <Button
                variant="ghost"
                className="w-full flex items-center justify-start gap-2 p-0 h-auto"
                onClick={handleBackToSocial}
              >
                <MdArrowBack className="w-4 h-4" />
                Back to other sign-in methods
              </Button>
              {isEmailSent ? (
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      We've sent a 6-digit code to
                    </p>
                    <p className="font-medium">{sentEmail}</p>
                  </div>
                  
                  <form onSubmit={handleSubmitCode(onSubmitCode)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Enter Code</Label>
                      <Input
                        id="code"
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-2xl tracking-widest font-mono"
                        {...registerCode("code", {
                          required: "Code is required",
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Code must be 6 digits",
                          },
                        })}
                        onChange={(e) => {
                          // Remove all non-digit characters and trim whitespace
                          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
                          e.target.value = cleaned;
                          // Manually trigger the react-hook-form onChange
                          registerCode("code").onChange(e);
                        }}
                        onPaste={(e) => {
                          // Handle paste event to clean the pasted content
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          const cleaned = pastedText.replace(/\D/g, '').slice(0, 6);
                          e.currentTarget.value = cleaned;
                          // Trigger react-hook-form onChange
                          registerCode("code").onChange({
                            target: { value: cleaned, name: "code" }
                          } as any);
                        }}
                        autoFocus
                      />
                      {codeErrors.code && (
                        <p className="text-sm text-red-500">{codeErrors.code.message}</p>
                      )}
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? "Verifying..." : "Verify Code"}
                    </Button>
                  </form>

                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                      onClick={() => {
                        setIsEmailSent(false);
                        setSentEmail("");
                      }}
                    >
                      Use a different email
                    </button>
                  </div>

                  <div className="grid gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => window.open("https://mail.google.com", "_blank")}
                    >
                      <SiGmail className="w-4 h-4" /> Open Gmail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => window.open("https://outlook.live.com", "_blank")}
                    >
                      <PiMicrosoftOutlookLogoFill className="w-4 h-4" /> Open Outlook
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmitEmail)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Submit"}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleGoogleLogin}>
                <span className="flex items-center gap-2">
                  <FcGoogle className="w-5 h-5" />
                  Sign in with Google
                </span>
                {lastUsedAuthnProvider === "google" ? lastUsedText : ""}
              </Button>
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleGitHubLogin}>
                <span className="flex items-center gap-2">
                  <FaGithub className="w-5 h-5" />
                  Sign in with Github
                </span>
                {lastUsedAuthnProvider === "github" ? lastUsedText : ""}
              </Button>
              {/* Separator between social sign-in options and email sign-in */}
              <div className="h-px w-full bg-muted" />
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleEmailLogin}>
                <span className="flex items-center gap-2">
                  <MdEmail className="w-5 h-5" />
                  Sign in with Email
                </span>
                {lastUsedAuthnProvider === "email" ? lastUsedText : ""}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
